import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode.react';
import styles from './Form.module.scss';
import Actions from '../actions/Actions';
import { useCourseContent } from '../../../../hooks/useCourseContent';
import { gql, useMutation } from '@apollo/client';
import { FeedbackFormContent, SurveyTypes } from '../../../../types';
import Button from '../../../../components/button/Button';
import iconSrc from './assets/icon.png';
import IconChain from '../../../../assets/icons/link_chain.svg?react';
import IconExclamation from '../../../../assets/icons/exclamation_sign.svg?react';
import { CopyToClipboard } from '@src/utils/componentCasts';
import { useToast } from 'src/hooks/useToast';
import Spinner from "../../../../components/spinner/Spinner";
import { useAnalytics, Events } from '@hooks';
import { useUser } from 'src/hooks/useUser';
import { ExitTicketExpirationTime, ResearchExpirationTime } from '../../../../constants';
import { logger } from '../../../../utils/logger';

type Props = {
  formData?: string | null;
  isFullScreen: boolean;
  id: string;
  name: string;
};

const createFeedbackFormMutation = gql`
    mutation CreateFeedbackFrom($input: FeedbackFormContentInput!) {
        createFeedbackForm(input: $input) {
            id, programName, lessonName
        }
    }
`;

type CreateFeedbackFormMutationResponse = {
  createFeedbackForm: FeedbackFormContent;
};

const createURL = (id: string) => {
  return `${window.location.origin}/ff/${id}`;
};

function Form(props: Props) {
  const { formData, isFullScreen, name } = props;
  const { program, lesson, content: contentValue } = useCourseContent();
  const [user] = useUser();
  const { successToast } = useToast();
  const [feedbackForm, setFeedbackForm] = useState<FeedbackFormContent | undefined>();
  const [timeLeft, setTimeLeft] = useState<number>(ExitTicketExpirationTime * 60);
  const [hideNextButton, setShowNextButton] = useState(true);
  const { trackEvent } = useAnalytics();

  const Expiration_time = useMemo(() => ((Number(JSON.parse(formData || '{}')?.surveyType) === 2 ? ResearchExpirationTime : ExitTicketExpirationTime) * 60), [formData]);

  const surveyType = useMemo(() => {
    let surveyTypeInt = Number(JSON.parse(formData || '{}')?.surveyType);
    switch (surveyTypeInt) {
      case 1:
        return SurveyTypes.ExitTickets;
      case 2:
        return SurveyTypes.Research;
      case 3:
        return SurveyTypes.PreProgram;
      case 4:
        return SurveyTypes.StudentQuiz;
      case 5:
        return SurveyTypes.PreProgram; // AU_PRE_PROGRAM_QUIZ
      case 6:
        return SurveyTypes.PostProgram; // POST_PROGRAM_QUIZ
      case 7:
        return SurveyTypes.AbsentStudent; // ABSENT_STUDENT
      default:
        return SurveyTypes.ExitTickets; // Default to Exit Tickets
    }
  }, [formData])

  let countDownTimer = useRef<NodeJS.Timeout | null>(null);
  const [createForm, { loading }] = useMutation<CreateFeedbackFormMutationResponse>(createFeedbackFormMutation, {
    onError(e) {
      logger.debug(`[e]`, e);
      setShowNextButton(false);
    },
    onCompleted(res) {
      setFeedbackForm(res.createFeedbackForm);
      countDownTimer.current = setInterval(() => {
        setTimeLeft(prevState => prevState - 60);
      }, 60 * 1000); // countdown every minute
      setShowNextButton(false);
      const _d = {
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(lesson),
        ...Events.mappers.content(contentValue),
        school_name: user?.schools?.[0]?.name || '',
        customer_name: user?.customer?.name || '',
        survey_type: surveyType,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };

      trackEvent(Events.actions.surveyFormLinkCreated, _d);
    }
  });

  useEffect(() => {
    if (timeLeft <= 0) {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current)
        countDownTimer.current = null;
      }
    }
  }, [timeLeft]);

  useEffect(() => {
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current)
        countDownTimer.current = null;
      }
    }
  }, []);

  const handleGenerateForm = useCallback(() => {
    setTimeLeft(Expiration_time);
    createForm({
      variables: {
        input: {
          programName: program!.name,
          lessonName: lesson!.name,
          programId: program!.id,
          lessonId: lesson!.id,
          formData,
        }
      }
    });
  }, [createForm, program, lesson, formData, Expiration_time]);

  const handleCopyToClipboard = useCallback(() => {
    successToast('Link Copied Successfully!');
  }, [successToast]);

  const generateLink = useCallback(() => {
    setFeedbackForm(undefined);
    setTimeLeft(Expiration_time);
    handleGenerateForm();
  }, [Expiration_time, handleGenerateForm]);

  const content = useMemo(() => {
    if (!feedbackForm) {
      const jsonFormData = JSON.parse(formData || '{}');
      return (
        <div className={styles.generateContent}>
          <div className={styles.qrLessonName}>{name}</div>
          {!!jsonFormData.instructions && <div className={styles.description}>{jsonFormData.instructions}</div>}
          {loading && <Spinner />}
          {!loading && <Button className={styles.generateBtn} label={'Continue'}
            onClick={handleGenerateForm} />}
        </div>
      );
    } else if (timeLeft > 0) {
      const url = createURL(feedbackForm.id);
      return (
        <div className={`${styles.qrContent} ${isFullScreen ? styles.qrContentWhite : ''}`}>
          <div className={styles.qrCodeWrapper}>
            <QRCode
              value={url}
              size={250}
              imageSettings={{ src: iconSrc, width: 24, height: 24, excavate: true }}
              level={'M'}
              renderAs={'svg'}
            />
          </div>
          <CopyToClipboard text={url} onCopy={() => handleCopyToClipboard()}>
            <div style={{ margin: '0 auto', display: 'block', marginBottom: 30 }}><Button title={url} className={styles.pulsedButton}
              leftIcon={<IconChain width="20px" height="20px" className={styles.iconWrapper} />}
              label={' Click here to copy link URL'} />
            </div>
          </CopyToClipboard>
          <div className={styles.qrValidityPeriod}>
            <div className={styles.row}>
              <IconExclamation />
              <div style={{ marginBottom: 10, fontWeight: "lighter" }}>To complete the survey, please do one of the following:</div>
            </div>
            <div className={styles.paragraph}>1. &nbsp; Scan the QR code and complete the survey on a mobile device;
              or
            </div>
            <div className={styles.paragraph}>2.&nbsp; Click the button above to copy the link to your clipboard and paste it
              in a web browser, or send it out to students.
            </div>
            <div className={styles.bottomParagraph}>{`You have ${Expiration_time / 60 === ExitTicketExpirationTime ? `${ExitTicketExpirationTime} minutes` : `${ResearchExpirationTime / 60} hours`} to begin the survey once the QR code has been
              generated. If the code expires, you can regenerate a unique Code/URL.`}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.generateContent}>
          <div className={styles.qrLessonName}>Survey Link Expired</div>
          <div className={styles.description}>Click button below to regenerate the Survey QR Code.</div>
          <Button className={styles.reGenerateBtn} label={'Regenerate Survey QR Code'}
            onClick={generateLink} />
        </div>
      )
    }
  }, [generateLink, Expiration_time, feedbackForm, formData, handleCopyToClipboard, handleGenerateForm, isFullScreen, loading, name, timeLeft]);

  if (!formData) return null;
  return (
    <Actions fullScreen={isFullScreen} className={styles.actionsWrapper} hideNextButton={hideNextButton}>
      {content}
    </Actions>
  );
}

export default Form;
